import {
  Lock,
  Smartphone,
  Shield,
  Eye,
  EyeOff,
  X,
  RefreshCw,
  AlertTriangle,
  DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { useMySessions, useProfile } from "@/hooks";
import Loader from "@/components/loader/Loader";
import { ErrorState } from "@/components/ErrorState";
import { useAppSelector } from "@/store/hooks";
import { queryClient } from "@/services/queryClient";
import TransactionPasswordSetting from "../shared/TransactionPasswordSetting";
import type { Session } from "@/interfaces/session";

const SecurityPage = () => {
  const [mode, setMode] = useState("");
  const [open, setOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id || "";

  const { data: profile, isLoading, isError, refetch } = useProfile(userId);
  const {
    data: sessions,
    isLoading: isLoadingSession,
    isError: isErrorSession,
    refetch: refetchSession,
  } = useMySessions(userId);

  console.log("SESSIONS", sessions);

  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [showAntiPhishingModal, setShowAntiPhishingModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  // Form states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [antiPhishingCode, setAntiPhishingCode] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const ToggleField = ({
    label,
    description,
    checked,
    onChange,
  }: {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (v: boolean) => void;
  }) => (
    <label className="flex items-center justify-between p-4 bg-muted rounded-xl cursor-pointer">
      <div>
        <p className="font-medium">{label}</p>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          checked ? "bg-yellow-500" : "bg-muted-foreground/30",
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
            checked ? "translate-x-6" : "translate-x-1",
          )}
        />
      </button>
    </label>
  );

  console.log("SESSIONS", sessions);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.warning("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.warning("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      toast.warning("Password must be at least 8 characters");
      return;
    }

    try {
      setIsProcessing(true);

      const res = await makeRequest.put("/auth/password/update", {
        oldPassword: currentPassword,
        newPassword,
      });

      if (res.data.success) {
        toast.success(res.data.message);
      }

      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const toggle2FA = async () => {
    try {
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggle2FA = async () => {
    const payload = {
      twoFAEnabled: profile?.setting?.twoFAEnabled ? false : true,
    };

    try {
      const res = await makeRequest.put("/setting/me", payload);
      if (res) {
        toast.success("2FA updated.");
        queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      }

      console.log(res);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSetAntiPhishingCode = () => {
    if (!antiPhishingCode.trim() || antiPhishingCode.length < 4) {
      toast.warning("Code must be at least 4 characters");
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      toast.success("Anti-phishing code set successfully");
      setShowAntiPhishingModal(false);
      setAntiPhishingCode("");
      setIsProcessing(false);
    }, 1000);
  };

  const handleRevokeSession = async () => {
    if (!selectedSession) return;

    try {
      setIsProcessing(true);

      const res = await makeRequest.delete(
        `/auth/sessions/${selectedSession.id}`,
      );

      if (res.data.success) {
        toast.success(res.data.message);

        queryClient.invalidateQueries({ queryKey: ["my-sessions", userId] });

        // setSessions((prev) => prev.filter((s) => s.id !== selectedSession.id));

        setShowRevokeModal(false);
        setSelectedSession(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const revokeAllOtherSessions = async () => {
    try {
      await makeRequest.delete("/auth/sessions/others");

      queryClient.invalidateQueries({ queryKey: ["my-sessions", userId] });
      toast.success("Other sessions revoked");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openRevokeModal = (session: Session) => {
    setSelectedSession(session);
    setShowRevokeModal(true);
  };

  if (isLoading || isLoading) return <Loader type="bounce" />;

  if (isError) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background lg:px-6">
      <div className="flex justify-end my-4">
        <Badge className="bg-emerald-500/20 text-emerald-500">
          <Shield className="w-3 h-3 mr-1" />
          Strong
        </Badge>
      </div>

      <div className="space-y-6">
        {/* Password */}
        <div className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">Login Password</h3>
              <p className="text-sm text-muted-foreground">
                Last changed 3 months ago
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              className="w-fit bg-primary"
              // variant="outline"
              onClick={() => setShowPasswordModal(true)}
            >
              Change Password
            </Button>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground">
                {" "}
                Transaction Password
              </h3>
              <p className="text-sm text-muted-foreground">
                Required for transaction authorization
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              className="w-fit bg-primary"
              // variant="outline"
              // size="sm"
              onClick={() => {
                setMode(profile?.isSecurityCodeVerified ? "update" : "create");
                setOpen(true);
              }}
            >
              {profile?.isSecurityCodeVerified ? "Update" : "Setup"}
            </Button>
          </div>
        </div>

        {/* 2FA */}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>

              <div>
                <p className="text-foreground font-medium">
                  2FA Authentication
                </p>

                <p className="text-xs text-muted-foreground">
                  Enable or disable 2FA
                </p>
              </div>
            </div>
            <button
              onClick={handleToggle2FA}
              className={cn(
                "relative w-12 h-6 rounded-full transition-colors",
                profile?.setting?.twoFAEnabled ? "bg-emerald-500" : "bg-muted",
              )}
            >
              <span
                className={cn(
                  "absolute top-1 w-4 h-4 rounded-full bg-white transition-all",
                  profile?.setting?.twoFAEnabled ? "left-7" : "left-1",
                )}
              />
            </button>
          </div>
        </div>

        {/* Login History */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="font-semibold text-foreground mb-4">
            Recent Login Activity
          </h3>
          <div className="space-y-3">
            {sessions?.map((session) => (
              <div
                key={session.id}
                className="flex justify-between p-4 bg-muted/50 rounded-lg flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-foreground font-medium flex items-center gap-2">
                      {session?.os}
                      {session.current && (
                        <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px]">
                          Current
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {session?.location?.city}, {session?.location?.country} •{" "}
                      {new Date(session.lastActivityAt ?? "").toLocaleString()}{" "}
                      • IP: {session?.location?.ip}
                    </p>
                  </div>
                </div>
                {!session.current && (
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => openRevokeModal(session)}
                    >
                      Revoke
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex items-center justify-center flex-col">
            <DialogTitle className="flex items-center justify-center flex-col gap-2">
              <Lock className="w-10 h-10 text-primary" />
              Change Password
            </DialogTitle>
            <DialogDescription className="text-center">
              Enter your current password and a new password to update your
              credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <Label htmlFor="current-password" className="mb-2">
                Current Password
              </Label>

              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="h-14 px-4 text-base pr-10"
                />

                <button
                  type="button"
                  onClick={() => setShowCurrentPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showCurrentPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="new-password" className="mb-2">
                New Password
              </Label>

              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 chars)"
                  className="h-14 px-4 text-base pr-10"
                />

                <button
                  type="button"
                  onClick={() => setShowNewPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirm-password" className="mb-2">
                Confirm New Password
              </Label>

              <div className="relative">
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="h-14 px-4 text-base pr-10"
                />

                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={isProcessing}
              className=" w-full h-14 px-4 text-base"
            >
              {isProcessing ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              Update Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Revoke Session Modal */}
      <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Revoke Session
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke this session? The user will be
              logged out immediately.
            </DialogDescription>
          </DialogHeader>
          {selectedSession && (
            <div className="p-4 bg-muted/50 rounded-lg my-4">
              <p className="font-medium">
                {selectedSession?.device?.deviceName}
              </p>
              <p className="text-sm text-muted-foreground">
                {selectedSession?.location?.country}
              </p>
              <p className="text-sm text-muted-foreground">
                IP: {selectedSession.ip}
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowRevokeModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleRevokeSession}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <X className="w-4 h-4 mr-2" />
              )}
              Revoke
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <TransactionPasswordSetting mode={mode} open={open} setOpen={setOpen} />
    </div>
  );
};

// QR Code Placeholder Component
const QRCodePlaceholder = () => (
  <svg viewBox="0 0 100 100" className="w-24 h-24">
    <rect x="10" y="10" width="25" height="25" fill="currentColor" />
    <rect x="65" y="10" width="25" height="25" fill="currentColor" />
    <rect x="10" y="65" width="25" height="25" fill="currentColor" />
    <rect x="15" y="15" width="15" height="15" fill="white" />
    <rect x="70" y="15" width="15" height="15" fill="white" />
    <rect x="15" y="70" width="15" height="15" fill="white" />
    <rect x="20" y="20" width="5" height="5" fill="currentColor" />
    <rect x="75" y="20" width="5" height="5" fill="currentColor" />
    <rect x="20" y="75" width="5" height="5" fill="currentColor" />
    <rect x="40" y="10" width="5" height="5" fill="currentColor" />
    <rect x="50" y="10" width="5" height="5" fill="currentColor" />
    <rect x="40" y="20" width="5" height="5" fill="currentColor" />
    <rect x="45" y="25" width="5" height="5" fill="currentColor" />
    <rect x="55" y="15" width="5" height="5" fill="currentColor" />
    <rect x="10" y="40" width="5" height="5" fill="currentColor" />
    <rect x="20" y="45" width="5" height="5" fill="currentColor" />
    <rect x="25" y="50" width="5" height="5" fill="currentColor" />
    <rect x="40" y="40" width="20" height="20" fill="currentColor" />
    <rect x="45" y="45" width="10" height="10" fill="white" />
    <rect x="48" y="48" width="4" height="4" fill="currentColor" />
    <rect x="65" y="40" width="5" height="5" fill="currentColor" />
    <rect x="75" y="45" width="5" height="5" fill="currentColor" />
    <rect x="85" y="50" width="5" height="5" fill="currentColor" />
    <rect x="40" y="65" width="5" height="5" fill="currentColor" />
    <rect x="50" y="70" width="5" height="5" fill="currentColor" />
    <rect x="55" y="75" width="5" height="5" fill="currentColor" />
    <rect x="65" y="65" width="25" height="25" fill="currentColor" />
    <rect x="70" y="70" width="15" height="15" fill="white" />
    <rect x="75" y="75" width="5" height="5" fill="currentColor" />
  </svg>
);

export default SecurityPage;
